import { FilterGroup } from 'components/Blocks/Product/Filter/FilterGroup/FilterGroup';
import { FilterGroupInStock } from 'components/Blocks/Product/Filter/FilterGroupInStock/FilterGroupInStock';
import { FilterGroupParameters } from 'components/Blocks/Product/Filter/FilterGroupParameters/FilterGroupParameters';
import { FilterGroupPrice } from 'components/Blocks/Product/Filter/FilterGroupPrice/FilterGroupPrice';
import Button from 'components/Forms/Button/Button';
import { ProductFilterOptionsFragmentApi } from 'graphql/generated';
import { useUpdateFilterQuery } from 'helpers/queryParams/useUpdateFilterQuery';
import { useDomainConfig } from 'hooks/useDomainConfig';
import useTranslation from 'next-translate/useTranslation';
import { RemoveIcon } from 'public/svg/IconsSvg';
import { memo } from 'react';
import { ParametersType } from 'types/productFilter';

type FilterPanelProps = {
    panelCloseHandler?: () => void;
    totalCount: number;
    productFilterOptions: ProductFilterOptionsFragmentApi;
};

const TEST_IDENTIFIER = 'blocks-product-filter';

const DEFAULT_NUMBER_OF_SHOWN_FLAGS = 5;
const DEFAULT_NUMBER_OF_SHOWN_PARAMETERS = 5;

export const FilterPanel = memo<FilterPanelProps>(
    ({ productFilterOptions: filterOptions, panelCloseHandler, totalCount }) => {
        const { t } = useTranslation();
        const { resetAllFilterQueries } = useUpdateFilterQuery();

        const { type } = useDomainConfig();

        return (
            <div
                className="relative z-aboveOverlay h-full rounded-none bg-grayLight pb-1 vl:z-above vl:h-auto vl:rounded-md"
                data-testid={TEST_IDENTIFIER}
            >
                <div className="absolute top-0 right-0 left-0 flex h-16 items-center justify-between bg-grayLight px-5 text-h3 leading-[32px] vl:hidden">
                    {t('Product filter')}
                    <span
                        className="relative inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-round border border-primary text-primary hover:bg-black hover:text-white"
                        onClick={panelCloseHandler}
                    >
                        <RemoveIcon className="h-2.5 w-2.5 text-currentColor transition-all duration-300" />
                    </span>
                </div>
                <div className="absolute top-16 right-0 left-0 bottom-16 overflow-y-scroll vl:static vl:overflow-visible">
                    {type !== 'web' && (
                        <>
                            <FilterGroupPrice
                                initialMaxPrice={filterOptions.maximalPrice}
                                initialMinPrice={filterOptions.minimalPrice}
                                title={t('Price')}
                            />

                            <FilterGroupInStock title={t('Availability')} inStockCount={filterOptions.inStock} />
                        </>
                    )}

                    {!!filterOptions.flags?.length && (
                        <FilterGroup
                            title={t('Flags')}
                            filterField="flags"
                            options={filterOptions.flags.map(({ flag, ...rest }) => ({ ...flag, ...rest }))}
                            defaultNumberOfShownItems={DEFAULT_NUMBER_OF_SHOWN_FLAGS}
                        />
                    )}

                    {filterOptions.parameters?.map((parameter, index) => (
                        <FilterGroupParameters
                            key={parameter.uuid}
                            defaultNumberOfShownParameters={DEFAULT_NUMBER_OF_SHOWN_PARAMETERS}
                            parameter={parameter as ParametersType}
                            parameterIndex={index}
                            title={parameter.name}
                        />
                    ))}
                </div>
                <div className="absolute right-0 left-0 bottom-0 flex h-16 items-center justify-between bg-white px-5 shadow-[0_0_10px_0_rgba(0,0,0,0.15)] vl:hidden">
                    <a className="-ml-2 inline-block cursor-pointer p-2 no-underline" onClick={resetAllFilterQueries}>
                        {t('Clear filters')}
                    </a>
                    <Button
                        type="button"
                        size="smallNarrow"
                        onClick={panelCloseHandler}
                        className="inline-block lowercase first-letter:uppercase"
                    >
                        {t('Show')}
                        {` ${totalCount} `}
                        {t('Products count', {
                            count: totalCount,
                        })}
                    </Button>
                </div>
            </div>
        );
    },
);

FilterPanel.displayName = 'FilterPanel';
